import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, 
  SimpleGrid, 
  useToast, 
  Flex, 
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Input,
  Select,
  Text,
  useDisclosure,
  Checkbox,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
} from '@chakra-ui/react';
import DynamicTable from '../../../components/table/DynamicTable.js';
import { columnsDataPriceIncreases } from './variables/columnsDataPriceIncreases.js';
import { getData, postData, putData, deleteData } from '../../../utils/httpClient.js';
import ConfirmModal from '../../../components/modal/ConfirmModal.js';

const initialFormData = {
  percentage: '',
  applicationDate: '',
  applicationType: 'courses_with_payment',
  selectedCourseIds: [],
};

export default function PriceIncreases() {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [availableCourses, setAvailableCourses] = useState([]);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isDeleteOpen, 
    onOpen: onDeleteOpen, 
    onClose: onDeleteClose 
  } = useDisclosure();

  const rowsPerPage = 10;
  const toast = useToast();

  const getPriceIncreases = useCallback(async (searchData = '') => {
    try {
      const response = await getData(
        `/priceIncreases?page=${page + 1}&limit=${rowsPerPage}&data=${searchData}`,
      );
      setData(response?.payload?.priceIncreases || []);
      setTotalPages(Math.ceil(response?.payload?.total / rowsPerPage));
    } catch (error) {
      toast({
        title: 'Error al obtener aumentos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [page, rowsPerPage, toast]);

  const getAvailableCourses = useCallback(async () => {
    try {
      const response = await getData('/priceIncreases/courses/available');
      setAvailableCourses(response?.payload?.courses || []);
    } catch (error) {
      toast({
        title: 'Error al obtener dictados',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  const createPriceIncrease = async () => {
    try {
      const priceIncreaseData = {
        percentage: parseFloat(formData.percentage),
        applicationDate: new Date(formData.applicationDate),
        applicationType: formData.applicationType,
        selectedCourseIds: formData.applicationType === 'specific_courses' ? formData.selectedCourseIds : [],
      };
      
      const response = await postData('/priceIncreases', priceIncreaseData);
      if (response?.payload) {
        toast({
          title: 'Aumento creado con éxito',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        closeModal();
        await getPriceIncreases('');
      }
    } catch (error) {
      toast({
        title: 'Error al crear aumento',
        description: error.response?.data?.message || 'Error interno del servidor',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const updatePriceIncrease = async () => {
    try {
      const priceIncreaseData = {
        percentage: parseFloat(formData.percentage),
        applicationDate: new Date(formData.applicationDate),
        applicationType: formData.applicationType,
      };
      
      const response = await putData(`/priceIncreases/${editingId}`, priceIncreaseData);
      if (response?.payload) {
        toast({
          title: 'Aumento actualizado con éxito',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        closeModal();
        await getPriceIncreases('');
      }
    } catch (error) {
      toast({
        title: 'Error al actualizar aumento',
        description: error.response?.data?.message || 'Error interno del servidor',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const deletePriceIncrease = async () => {
    try {
      await deleteData(`/priceIncreases/${deleteId}`);
      toast({
        title: 'Aumento desactivado con éxito',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteClose();
      setDeleteId(null);
      await getPriceIncreases('');
    } catch (error) {
      toast({
        title: 'Error al desactivar aumento',
        description: error.response?.data?.message || 'Error interno del servidor',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getPriceIncreases('');
    getAvailableCourses();
  }, [page, getPriceIncreases, getAvailableCourses]);

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingId(null);
  };

  const openModal = () => {
    resetForm();
    onOpen();
  };

  const closeModal = () => {
    resetForm();
    onClose();
  };

  const handleEdit = (priceIncrease) => {
    // Convertir la fecha al formato YYYY-MM-DD para el input de fecha
    const applicationDate = priceIncrease.applicationDate 
      ? new Date(priceIncrease.applicationDate).toISOString().split('T')[0]
      : '';
    
    setFormData({
      percentage: priceIncrease.percentage.toString(),
      applicationDate: applicationDate,
      applicationType: priceIncrease.applicationType || 'courses_with_payment',
      selectedCourseIds: priceIncrease.selectedCourseIds || [],
    });
    setEditingId(priceIncrease.id);
    onOpen();
  };

  const handleDelete = (priceIncrease) => {
    setDeleteId(priceIncrease.id);
    onDeleteOpen();
  };

  const handleSubmit = async () => {
    if (!formData.percentage || parseFloat(formData.percentage) <= 0) {
      toast({
        title: 'Por favor ingrese un porcentaje válido',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.applicationDate) {
      toast({
        title: 'Por favor seleccione una fecha de aplicación',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (formData.applicationType === 'specific_courses' && formData.selectedCourseIds.length === 0) {
      toast({
        title: 'Por favor seleccione al menos un dictado',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (editingId) {
      await updatePriceIncrease();
    } else {
      await createPriceIncrease();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: value,
      // Limpiar cursos seleccionados si no es "specific_courses"
      selectedCourseIds: name === 'applicationType' && value !== 'specific_courses' ? [] : prev.selectedCourseIds
    }));
  };

  const handleCourseSelection = (courseId) => {
    setFormData(prev => ({
      ...prev,
      selectedCourseIds: prev.selectedCourseIds.includes(courseId)
        ? prev.selectedCourseIds.filter(id => id !== courseId)
        : [...prev.selectedCourseIds, courseId]
    }));
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="2xl" fontWeight="bold">
          Gestión de Aumentos
        </Text>
        <Button colorScheme="blue" onClick={openModal}>
          Nuevo Aumento
        </Button>
      </Flex>
      
      <SimpleGrid
        mb="20px"
        columns={{ sm: 1 }}
        spacing={{ base: '20px', xl: '20px' }}
      >
        <DynamicTable
          columnsData={columnsDataPriceIncreases}
          data={data}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </SimpleGrid>

      {/* Modal para crear/editar aumento */}
      <Modal isOpen={isOpen} onClose={closeModal} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh">
          <ModalHeader>
            {editingId ? 'Editar Aumento' : 'Nuevo Aumento'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH="70vh" overflowY="auto">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FormControl isRequired>
                <FormLabel>Porcentaje de Aumento</FormLabel>
                <NumberInput min={0} max={100} precision={2}>
                  <NumberInputField
                    name="percentage"
                    value={formData.percentage}
                    onChange={handleInputChange}
                    placeholder="Ej: 15.50"
                  />
                </NumberInput>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Fecha de Aplicación</FormLabel>
                <Input
                  type="date"
                  name="applicationDate"
                  value={formData.applicationDate}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]} // No permitir fechas pasadas
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Fecha a partir de la cual se aplicará el aumento
                </Text>
              </FormControl>
            </SimpleGrid>

            <FormControl isRequired mt={6}>
              <FormLabel>Aplicar aumento a:</FormLabel>
              <Select
                name="applicationType"
                value={formData.applicationType}
                onChange={handleInputChange}
              >
                <option value="courses_with_payment">Dictados con modalidad de pago</option>
                <option value="all_courses">Todos los dictados</option>
                <option value="specific_courses">Dictados específicos</option>
              </Select>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Seleccione el tipo de dictados a los que se aplicará el aumento
              </Text>
            </FormControl>

            {formData.applicationType === 'specific_courses' && (
              <FormControl isRequired mt={6}>
                <FormLabel>Seleccionar Dictados:</FormLabel>
                <Box 
                  border="1px" 
                  borderColor="gray.200" 
                  borderRadius="md" 
                  p={3} 
                  maxH="500px" 
                  overflowY="auto"
                >
                  {availableCourses.length > 0 ? (
                    <TableContainer>
                      <Table size="sm" variant="striped" colorScheme="gray">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th width="25%">Estudio</Th>
                            <Th width="20%">Ciclo</Th>
                            <Th width="25%">Materia</Th>
                            <Th width="15%" textAlign="right">Monto</Th>
                            <Th width="15%" textAlign="center">Estado</Th>
                            <Th width="60px" textAlign="center">Selec.</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {availableCourses.map((course) => (
                            <Tr key={course.id} _hover={{ bg: "blue.50" }}>
                              <Td>
                                <Text fontSize="sm" fontWeight="medium" noOfLines={2}>
                                  {course.study}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize="sm" noOfLines={2}>
                                  {course.cycle}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontSize="sm" noOfLines={2}>
                                  {course.subject}
                                </Text>
                              </Td>
                              <Td textAlign="right">
                                <Text fontSize="sm" fontWeight="bold" color="green.600">
                                  ${course.inscriptionAmount?.toLocaleString() || '0'}
                                </Text>
                              </Td>
                              <Td textAlign="center">
                                <Badge 
                                  colorScheme={course.hasPaymentMethods ? 'green' : 'gray'} 
                                  size="sm"
                                  variant="subtle"
                                >
                                  {course.hasPaymentMethods ? 'Con modalidad' : 'Sin modalidad'}
                                </Badge>
                              </Td>
                              <Td textAlign="center">
                                <Checkbox
                                  isChecked={formData.selectedCourseIds.includes(course.id)}
                                  onChange={() => handleCourseSelection(course.id)}
                                  colorScheme="blue"
                                />
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                      No hay dictados disponibles
                    </Text>
                  )}
                </Box>
                {formData.selectedCourseIds.length > 0 && (
                  <Box mt={4} p={3} bg="blue.50" borderRadius="md" border="1" borderColor="blue.200">
                    <HStack justify="space-between" align="center">
                      <HStack>
                        <Badge colorScheme="blue" variant="solid" fontSize="sm">
                          {formData.selectedCourseIds.length}
                        </Badge>
                        <Text fontSize="sm" color="blue.700" fontWeight="medium">
                          dictado(s) seleccionado(s) para aplicar el aumento
                        </Text>
                      </HStack>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        colorScheme="blue"
                        onClick={() => setFormData(prev => ({ ...prev, selectedCourseIds: [] }))}
                      >
                        Limpiar selección
                      </Button>
                    </HStack>
                  </Box>
                )}
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeModal}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {editingId ? 'Actualizar' : 'Crear'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de confirmación para eliminar */}
      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={deletePriceIncrease}
        header="Confirmar Desactivación"
        message="¿Estás seguro de que deseas desactivar este aumento? Esta acción no se puede deshacer."
      />
    </Box>
  );
}
